import { useGetAppliedDiscountsQuery } from 'store/queries/bolt-backend/service-contracts'
import { selectCurrentServiceContracts } from 'store/contact/slice.ts'
import { useStoreSelector } from 'hooks/store.ts'
import { Card, Heading, LoadingSkeleton } from '@boltenergy-be/design-system'
import Link from 'components/link/Link.tsx'
import { routes } from 'types/routes.ts'
import { useTranslation } from 'react-i18next'
import styles from './DiscountCard.module.scss'
import { Discount } from 'store/queries/bolt-backend/service-contracts/types.ts'
import { memoizedSelectActiveDiscounts } from 'store/queries/bolt-backend/service-contracts/selectors.ts'
import dayjs from 'dayjs'
import { DATE_FORMAT } from 'constants/constants.ts'

// Stable array reference instead of re-created array each time the component renders.
const EMPTY_ARRAY_REFERENCE: Discount[] = []

const DiscountCard = () => {
  // Redux
  const { electricity, gas } = useStoreSelector(selectCurrentServiceContracts)

  // Queries
  const { active: elActive, isLoading: elLoading } = useGetAppliedDiscountsQuery(
    { contractNumber: electricity.contractNumber },
    {
      skip: !electricity.contractNumber,
      selectFromResult: ({ data, isLoading }) => ({
        active: memoizedSelectActiveDiscounts(data?.discounts || EMPTY_ARRAY_REFERENCE),
        isLoading
      })
    }
  )
  const { active: gasActive, isLoading: gasLoading } = useGetAppliedDiscountsQuery(
    { contractNumber: gas?.contractNumber },
    {
      skip: !gas?.contractNumber,
      selectFromResult: ({ data, isLoading }) => ({
        active: memoizedSelectActiveDiscounts(data?.discounts || EMPTY_ARRAY_REFERENCE),
        isLoading
      })
    }
  )

  // i18n
  const { t } = useTranslation('tariffs', { keyPrefix: 'overview.discounts' })

  // Constants
  const isLoading = elLoading || gasLoading
  const activeDiscounts = [...(elActive || []), ...(gasActive || [])]

  return (
    <Card as="section" className={styles['discount-card']} color="neutral">
      <Heading as="h3" variant="h6" serif>
        {t(($) => $.label)}
      </Heading>

      {isLoading ? (
        <LoadingSkeleton>
          <LoadingSkeleton.Paragraph />
        </LoadingSkeleton>
      ) : activeDiscounts?.length ? (
        <ul className={styles['discount-list']}>
          {activeDiscounts.map((discount) => (
            <li
              key={`${discount.promoCode}-${discount.energyType}-${discount.direction}-${dayjs(discount.fromDate).unix()}`}
              className={styles.discount}
            >
              <strong>{discount.discount}</strong>
              <small>
                {t(($) => $.activeUntil, {
                  date: dayjs(discount.toDate).format(DATE_FORMAT),
                  interpolation: { escapeValue: false }
                })}
              </small>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-subdued text-xs">{t(($) => $.none)}</p>
      )}

      <Link representation="button" href={routes.TARIFFS_DISCOUNTS}>
        {t(($) => $.action)}
      </Link>
    </Card>
  )
}

export default DiscountCard
